import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  Send,
  ArrowLeft,
  User,
  Clock,
  CheckCircle,
  RefreshCw,
  AlertCircle,
  Loader2,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatDate } from '../../../utils/stringUtils';
import { get, post } from '../../../api/apiClient';
import locationService from '../../../api/services/locationService';
import exitService from '../../../api/services/exitService';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';

const RelievingLetterDetail = ({ id: propId, onClose, isDrawer }) => {
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const navigate = useNavigate();
  const [locations, setLocations] = React.useState([]);
  const [showSendModal, setShowSendModal] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);

  // Fetch relieving letter by ID
  const { data: letterData, isLoading, error } = useQuery({
    queryKey: ['relievingLetter', id],
    queryFn: async () => {
      const result = await get(`/relieving-letter/${id}`);
      return result.data || result;
    },
    enabled: !!id,
  });

  // Load locations for PDF generation
  React.useEffect(() => {
    const loadLocations = async () => {
      try {
        const result = await locationService.getLocations({ isActive: 'all', limit: 1000 });
        if (result.success) {
          let locationsList = [];
          if (result.data?.data?.locations && Array.isArray(result.data.data.locations)) {
            locationsList = result.data.data.locations;
          } else if (result.data?.data && Array.isArray(result.data.data)) {
            locationsList = result.data.data;
          } else if (result.data?.locations && Array.isArray(result.data.locations)) {
            locationsList = result.data.locations;
          } else if (Array.isArray(result.data)) {
            locationsList = result.data;
          }
          setLocations(locationsList);
        }
      } catch (error) {
      }
    };
    loadLocations();
  }, []);

  const letter = letterData;

  // Download letter as PDF
  const handleDownloadLetter = async () => {
    if (!letter) return;

    try {
      const fileName = `Relieving_Letter_${letter.employeeName?.replace(/\s+/g, '_')}_${letter.letterId}.pdf`;
      const blob = await get(`/relieving-letter/${letter._id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(`❌ Failed to download relieving letter PDF: ${error.message || 'Server error occurred.'}`);
    }
  };

  const handleExport = async () => {
    if (isExporting || !id) return;
    setIsExporting(true);
    const toastId = toast.loading('Preparing Excel export...');
    try {
      const exportName = letter?.letterId || letter?.employeeName || 'RelievingLetter';
      const result = await exitService.exportRelievingLetterDetail(id, exportName);
      if (result.success) {
        toast.success('Relieving letter exported successfully', { id: toastId });
      } else {
        toast.error(result.error || 'Failed to export relieving letter', { id: toastId });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to export relieving letter', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  // Send letter
  const handleSendLetter = async () => {
    if (!letter) return;

    setIsSending(true);

    try {
      // Send to backend - PDF is now generated on backend
      await post(`/relieving-letter/${letter._id}/send`, {
        emailContent: null, // Use default email template
      });
      alert(`Relieving letter sent successfully to ${letter.employeeEmail || letter.employeeName}`);
      setShowSendModal(false);
    } catch (error) {
      alert('Failed to send relieving letter email: ' + error.message);
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex items-center space-x-3">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading relieving letter details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Letter</h2>
          <p className="text-gray-600 mb-4">{error.message || 'Failed to load relieving letter'}</p>
          <button
            onClick={() => navigate('/relieving_letter')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Back to Relieving Letters
          </button>
        </div>
      </div>
    );
  }

  if (!letter) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Letter Not Found</h2>
          <p className="text-gray-600 mb-4">The relieving letter you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/relieving_letter')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Back to Relieving Letters
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Relieving Letter Details</h1>
            <p className="text-sm text-gray-500 mt-1">Letter ID: {letter.letterId}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <PermissionGate permission="exit_management" action="read">
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
          </PermissionGate>
          {letter.status === 'pending' && (
            <PermissionGate permission="exit_management" action="update">
              <button
                onClick={() => setShowSendModal(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 transition-colors flex items-center"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Letter
              </button>
            </PermissionGate>
          )}
          <PermissionGate permission="exit_management" action="read">
            <button
              onClick={handleDownloadLetter}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Letter
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <div className="space-y-6">
            {/* Employee Information */}
            <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-600" />
                Employee Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Name:</span>
                  <p className="text-gray-900 mt-1 break-words">{letter.employeeName || 'N/A'}</p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Employee ID:</span>
                  <p className="text-gray-900 mt-1 break-words">{letter.employeeIdNumber || 'N/A'}</p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Email:</span>
                  <p className="text-gray-900 mt-1 break-words">{letter.employeeEmail || 'N/A'}</p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Department:</span>
                  <p className="text-gray-900 mt-1 break-words capitalize">{letter.department || 'N/A'}</p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Designation:</span>
                  <p className="text-gray-900 mt-1 break-words capitalize">{letter.employeeId?.employmentInfo?.designation || letter.position || 'N/A'}</p>
                </div>
                {letter.employeeId?.personalInfo?.gender && (
                  <div className="min-w-0">
                    <span className="text-gray-600 font-medium">Gender:</span>
                    <p className="text-gray-900 mt-1 break-words capitalize">{letter.employeeId.personalInfo.gender}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Employment Details */}
            <div className="bg-green-50 rounded-lg p-5 border border-green-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-green-600" />
                Employment Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Join Date:</span>
                  <p className="text-gray-900 mt-1 break-words">{letter.employmentDetails?.joinDate ? formatDate(letter.employmentDetails.joinDate, { includeTime: false }) : 'N/A'}</p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Last Working Date:</span>
                  <p className="text-gray-900 mt-1 break-words">{letter.employmentDetails?.lastWorkingDate ? formatDate(letter.employmentDetails.lastWorkingDate, { includeTime: false }) : 'N/A'}</p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Total Experience:</span>
                  <p className="text-gray-900 mt-1 break-words">{letter.employmentDetails?.totalExperience || 'N/A'}</p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Employment Type:</span>
                  <p className="text-gray-900 mt-1 break-words capitalize">{letter.employmentDetails?.employmentType?.replace('-', ' ') || 'N/A'}</p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Work Location:</span>
                  <p className="text-gray-900 mt-1 break-words capitalize">{typeof letter.employmentDetails?.workLocation === 'object' ? (letter.employmentDetails?.workLocation?.name || letter.employmentDetails?.workLocation?.label || JSON.stringify(letter.employmentDetails?.workLocation)) : letter.employmentDetails?.workLocation || 'N/A'}</p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Reporting Manager:</span>
                  <p className="text-gray-900 mt-1 break-words">{typeof letter.employmentDetails?.reportingManager === 'object' ? letter.employmentDetails?.reportingManager?.name : letter.employmentDetails?.reportingManager || 'N/A'}</p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Reason for Leaving:</span>
                  <p className="text-gray-900 mt-1 break-words capitalize">{letter.employmentDetails?.reasonForLeaving?.replace('_', ' ') || 'N/A'}</p>
                </div>
                {letter.employmentDetails?.reasonDescription && (
                  <div className="md:col-span-2 lg:col-span-3 min-w-0">
                    <span className="text-gray-600 font-medium">Reason Description:</span>
                    <p className="text-gray-900 mt-1 whitespace-pre-wrap break-words">{letter.employmentDetails.reasonDescription}</p>
                  </div>
                )}
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Last Salary:</span>
                  <p className="text-gray-900 mt-1 font-semibold break-words">
                    {letter.financialDetails?.lastSalary
                      ? `${letter.financialDetails.currency || 'INR'} ${letter.financialDetails.lastSalary.toLocaleString()} / LPA`
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Performance Details */}
            <div className="bg-purple-50 rounded-lg p-5 border border-purple-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 text-purple-600" />
                Performance Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="min-w-0">
                  <span className="text-gray-600 font-medium">Overall Rating:</span>
                  <p className="text-gray-900 mt-1 break-words capitalize">{letter.performanceDetails?.overallRating?.replace('_', ' ') || 'N/A'}</p>
                </div>
                {letter.performanceDetails?.skills && letter.performanceDetails.skills.length > 0 && (
                  <div className="min-w-0">
                    <span className="text-gray-600 font-medium">Skills:</span>
                    <p className="text-gray-900 mt-1 break-words capitalize">{letter.performanceDetails.skills.join(', ') || 'N/A'}</p>
                  </div>
                )}
                {letter.performanceDetails?.performanceNotes && (
                  <div className="md:col-span-2 min-w-0">
                    <span className="text-gray-600 font-medium">Performance Notes:</span>
                    <p className="text-gray-900 mt-1 whitespace-pre-wrap break-words first-letter:capitalize">{letter.performanceDetails.performanceNotes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Confirmation Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Send Relieving Letter</h3>
                <button
                  onClick={() => setShowSendModal(false)}
                  disabled={isSending}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 break-words">
                Are you sure you want to send the relieving letter to <span className="font-semibold break-words">{letter.employeeEmail || 'email address'}</span>?
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowSendModal(false)}
                disabled={isSending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSendLetter}
                disabled={isSending}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelievingLetterDetail;

